import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { deleteDoc, doc, onSnapshot, orderBy, updateDoc, writeBatch } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../hooks/useAuth";
import { useCouple } from "../hooks/useCouple";
import { useCoupleCollection } from "../hooks/useCoupleCollection";
import { Sheet } from "../components/Sheet";
import type { ListItem, WirList } from "../types";

// Presets for the shopping-list use case specifically — a generic "normal"
// list just never uses these (no item gets a category unless picked), so
// nothing changes for lists that aren't shopping lists.
const SHOPPING_CATEGORIES = ["Obst & Gemüse", "Milchprodukte", "Fleisch & Fisch", "Brot & Backwaren", "Getränke", "Tiefkühl", "Drogerie", "Sonstiges"];

async function updateListName(coupleId: string, listId: string, name: string) {
  await updateDoc(doc(db, "couples", coupleId, "lists", listId), { name });
}

async function deleteList(coupleId: string, listId: string, items: ListItem[]) {
  for (let i = 0; i < items.length; i += 450) {
    const batch = writeBatch(db);
    for (const item of items.slice(i, i + 450)) {
      batch.delete(doc(db, "couples", coupleId, "lists", listId, "items", item.id));
    }
    await batch.commit();
  }
  await deleteDoc(doc(db, "couples", coupleId, "lists", listId));
}

export function ListDetail() {
  const { listId } = useParams<{ listId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { couple, profile } = useCouple();
  const coupleId = couple?.id ?? null;

  const [list, setList] = useState<WirList | null>(null);
  const [listLoading, setListLoading] = useState(true);
  useEffect(() => {
    if (!coupleId || !listId) return;
    setListLoading(true);
    return onSnapshot(doc(db, "couples", coupleId, "lists", listId), (snap) => {
      setList(snap.exists() ? ({ id: snap.id, ...snap.data() } as WirList) : null);
      setListLoading(false);
    });
  }, [coupleId, listId]);

  const { items, loading, add, patch, remove } = useCoupleCollection<ListItem>(
    coupleId,
    listId ? `lists/${listId}/items` : "lists/_/items",
    orderBy("createdAt", "asc")
  );

  // Falls back to inferring "shopping" for lists that predate the explicit
  // `type` field: the migrated legacy shopping list (fixed 🛒 icon) and any
  // list that already has categorized items, so nothing already in use
  // silently loses its categories. A brand-new "general" list never shows
  // these chips, which is the complaint this fixes — they used to show on
  // every list regardless of what it was for.
  const isShoppingList = list?.type ? list.type === "shopping" : list?.icon === "🛒" || items.some((i) => i.category);

  // Always-visible quick-add (with an optional category chip) instead of a
  // Fab+Sheet tap — adding an item is the single most frequent action on
  // this page, so that extra tap was pure friction.
  const [quickText, setQuickText] = useState("");
  const [quickCategory, setQuickCategory] = useState<string | null>(null);
  const [quickSubmitting, setQuickSubmitting] = useState(false);

  async function handleQuickAdd(e: FormEvent) {
    e.preventDefault();
    if (quickSubmitting || !quickText.trim() || !user || !profile) return;
    setQuickSubmitting(true);
    try {
      await add({ text: quickText.trim(), done: false, category: quickCategory, createdByUid: user.uid, createdByName: profile.displayName });
      setQuickText("");
    } finally {
      setQuickSubmitting(false);
    }
  }

  // Editing an existing item still uses a Sheet (less frequent action).
  const [text, setText] = useState("");
  const [editCategory, setEditCategory] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [manageOpen, setManageOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  function openEdit(item: ListItem) {
    setEditingId(item.id);
    setText(item.text);
    setEditCategory(item.category ?? null);
    setSheetOpen(true);
  }

  function closeSheet() {
    setSheetOpen(false);
    setEditingId(null);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting || !text.trim() || !editingId) return;
    setSubmitting(true);
    try {
      await patch(editingId, { text: text.trim(), category: editCategory });
      closeSheet();
    } finally {
      setSubmitting(false);
    }
  }

  // Grouped by category only once at least one item actually has one, so a
  // plain "normal" list (no categories ever picked) keeps its flat look.
  const hasCategories = isShoppingList && items.some((i) => i.category);
  const openGroups = useMemo(() => {
    const open = items.filter((i) => !i.done);
    if (!hasCategories) return [{ category: null, items: open }];
    const groups: { category: string | null; items: ListItem[] }[] = SHOPPING_CATEGORIES.map((c) => ({
      category: c,
      items: open.filter((i) => i.category === c),
    })).filter((g) => g.items.length > 0);
    const uncategorized = open.filter((i) => !i.category);
    if (uncategorized.length > 0) groups.push({ category: null, items: uncategorized });
    return groups;
  }, [items, hasCategories]);

  function openManage() {
    setNewName(list?.name ?? "");
    setRenaming(false);
    setConfirmDelete(false);
    setManageOpen(true);
  }

  async function handleRename(e: FormEvent) {
    e.preventDefault();
    if (!newName.trim() || !coupleId || !listId) return;
    await updateListName(coupleId, listId, newName.trim());
    setManageOpen(false);
  }

  async function handleDeleteList() {
    if (!coupleId || !listId) return;
    await deleteList(coupleId, listId, items);
    navigate("/lists", { replace: true });
  }

  async function toggleShoppingType() {
    if (!coupleId || !listId) return;
    await updateDoc(doc(db, "couples", coupleId, "lists", listId), { type: isShoppingList ? "general" : "shopping" });
  }

  const done = items.filter((i) => i.done);

  return (
    <main className="mx-auto min-h-dvh max-w-md px-5 pb-28 pt-6">
      <Link to="/lists" className="inline-flex items-center gap-1 text-sm font-medium text-plum-700 dark:text-night-accent">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-3.5 w-3.5">
          <path d="M15 5 8 12l7 7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Listen
      </Link>

      <div className="mt-2 flex items-center justify-between">
        <h1 className="flex items-center gap-2 font-display text-[1.75rem] font-semibold tracking-tight text-plum-900 dark:text-night-accent">
          <span>{list?.icon ?? "📝"}</span>
          <span className="truncate">{listLoading ? "…" : list?.name ?? "Liste"}</span>
        </h1>
        <button onClick={openManage} aria-label="Liste verwalten" className="flex-shrink-0 text-ink-400 dark:text-night-faint">
          <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
            <circle cx="5" cy="12" r="1.6" />
            <circle cx="12" cy="12" r="1.6" />
            <circle cx="19" cy="12" r="1.6" />
          </svg>
        </button>
      </div>

      <form onSubmit={handleQuickAdd} className="mt-4 space-y-2">
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Eintrag…"
            value={quickText}
            onChange={(e) => setQuickText(e.target.value)}
            className="h-12 flex-1 rounded-xl border border-cream-200 bg-white px-4 text-base text-ink-900 outline-none focus:border-plum-600 dark:border-night-border dark:bg-night-surface-raised dark:text-night-text"
          />
          <button
            type="submit"
            disabled={!quickText.trim() || quickSubmitting}
            aria-label="Eintrag hinzufügen"
            className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-plum-700 text-cream-50 disabled:opacity-50"
          >
            {quickSubmitting ? (
              "…"
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-5 w-5">
                <path d="M12 5v14M5 12h14" strokeLinecap="round" />
              </svg>
            )}
          </button>
        </div>
        {isShoppingList && (
          <div className="flex gap-1.5 overflow-x-auto pb-1">
            {SHOPPING_CATEGORIES.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setQuickCategory(quickCategory === c ? null : c)}
                className={`flex-shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${
                  quickCategory === c ? "border-plum-700 bg-plum-700 text-cream-50" : "border-cream-200 text-ink-400 dark:border-night-border dark:text-night-faint"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        )}
      </form>

      {loading ? (
        <p className="mt-6 text-sm text-ink-400 dark:text-night-faint">Lädt…</p>
      ) : items.length === 0 ? (
        <p className="mt-6 text-sm text-ink-400 dark:text-night-faint">Die Liste ist leer.</p>
      ) : (
        <>
          {openGroups.map((group) => (
            <div key={group.category ?? "__none__"} className="mt-5">
              {hasCategories && (
                <p className="mb-2 text-xs font-medium uppercase tracking-wider text-ink-400 dark:text-night-faint">
                  {group.category ?? "Ohne Kategorie"}
                </p>
              )}
              <ul className="space-y-2">
                {group.items.map((item) => (
                  <ListItemRow key={item.id} item={item} onToggle={() => patch(item.id, { done: true })} onEdit={() => openEdit(item)} onRemove={() => remove(item.id)} />
                ))}
              </ul>
            </div>
          ))}
          {done.length > 0 && (
            <>
              <p className="mt-6 text-xs font-medium uppercase tracking-wider text-ink-400 dark:text-night-faint">Erledigt</p>
              <ul className="mt-2 space-y-2">
                {done.map((item) => (
                  <ListItemRow key={item.id} item={item} onToggle={() => patch(item.id, { done: false })} onEdit={() => openEdit(item)} onRemove={() => remove(item.id)} />
                ))}
              </ul>
            </>
          )}
        </>
      )}

      <Sheet open={sheetOpen} onClose={closeSheet} title="Eintrag bearbeiten">
        <form onSubmit={handleSubmit} className="space-y-3">
          <input
            type="text"
            placeholder="Eintrag…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="h-12 w-full rounded-xl border border-cream-200 bg-white px-4 text-base text-ink-900 outline-none focus:border-plum-600 dark:border-night-border dark:bg-night-surface-raised dark:text-night-text"
          />
          {isShoppingList && (
            <div className="flex flex-wrap gap-1.5">
              {SHOPPING_CATEGORIES.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setEditCategory(editCategory === c ? null : c)}
                  className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${
                    editCategory === c ? "border-plum-700 bg-plum-700 text-cream-50" : "border-cream-200 text-ink-400 dark:border-night-border dark:text-night-faint"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          )}
          <button
            type="submit"
            disabled={!text.trim() || submitting}
            className="h-12 w-full rounded-xl bg-plum-700 text-sm font-semibold text-cream-50 disabled:opacity-50"
          >
            {submitting ? "Wird gespeichert…" : "Änderungen speichern"}
          </button>
        </form>
      </Sheet>

      <Sheet open={manageOpen} onClose={() => setManageOpen(false)} title="Liste verwalten">
        {confirmDelete ? (
          <div>
            <p className="text-sm text-ink-600 dark:text-night-muted">
              „{list?.name}" wird mit allen {items.length} Einträgen unwiderruflich gelöscht.
            </p>
            <div className="mt-5 flex gap-3">
              <button onClick={() => setConfirmDelete(false)} className="h-11 flex-1 rounded-xl border border-cream-200 text-sm font-semibold text-ink-900 dark:border-night-border dark:text-night-text">
                Abbrechen
              </button>
              <button onClick={handleDeleteList} className="h-11 flex-1 rounded-xl bg-garnet-600 text-sm font-semibold text-cream-50">
                Endgültig löschen
              </button>
            </div>
          </div>
        ) : renaming ? (
          <form onSubmit={handleRename} className="flex gap-2">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="h-12 flex-1 rounded-xl border border-cream-200 bg-white px-4 text-base text-ink-900 outline-none focus:border-plum-600 dark:border-night-border dark:bg-night-surface-raised dark:text-night-text"
            />
            <button type="submit" disabled={!newName.trim()} className="h-12 flex-shrink-0 rounded-xl bg-plum-700 px-4 text-sm font-semibold text-cream-50 disabled:opacity-50">
              OK
            </button>
          </form>
        ) : (
          <div className="space-y-2">
            <button
              onClick={() => setRenaming(true)}
              className="flex h-12 w-full items-center rounded-xl px-3 text-left text-sm font-medium text-ink-900 hover:bg-cream-100 dark:text-night-text dark:hover:bg-night-surface-raised"
            >
              Liste umbenennen
            </button>
            <div className="flex items-center justify-between rounded-xl bg-cream-100 px-3 py-3 dark:bg-night-surface-raised">
              <div className="min-w-0 pr-3">
                <p className="text-sm font-medium text-ink-900 dark:text-night-text">Lebensmittel-Kategorien</p>
                <p className="mt-0.5 text-xs text-ink-400 dark:text-night-faint">Für Einkaufslisten.</p>
              </div>
              <button
                onClick={toggleShoppingType}
                aria-pressed={isShoppingList}
                className={`relative h-7 w-12 flex-shrink-0 rounded-full transition-colors ${isShoppingList ? "bg-plum-700" : "bg-cream-200 dark:bg-night-border"}`}
              >
                <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-[left] ${isShoppingList ? "left-6" : "left-1"}`} />
              </button>
            </div>
            <button
              onClick={() => setConfirmDelete(true)}
              className="flex h-12 w-full items-center rounded-xl px-3 text-left text-sm font-medium text-garnet-600 hover:bg-garnet-100 dark:hover:bg-garnet-600/15"
            >
              Liste löschen
            </button>
          </div>
        )}
      </Sheet>
    </main>
  );
}

function ListItemRow({
  item,
  onToggle,
  onEdit,
  onRemove,
}: {
  item: ListItem;
  onToggle: () => void;
  onEdit: () => void;
  onRemove: () => void;
}) {
  return (
    <li className="card flex items-center gap-3 rounded-xl p-3.5">
      <button
        onClick={onToggle}
        aria-label={item.done ? "Als offen markieren" : "Als erledigt markieren"}
        className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border-2 ${
          item.done ? "border-plum-700 bg-plum-700 text-cream-50" : "border-cream-200 dark:border-night-border"
        }`}
      >
        {item.done && (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" className="h-3.5 w-3.5">
            <path d="M5 12.5 9.5 17 19 7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>
      <button
        onClick={onEdit}
        className={`min-w-0 flex-1 truncate text-left text-sm ${item.done ? "text-ink-400 line-through dark:text-night-faint" : "text-ink-900 dark:text-night-text"}`}
      >
        {item.text}
      </button>
      <button onClick={onEdit} aria-label="Bearbeiten" className="flex-shrink-0 text-ink-400 dark:text-night-faint">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4.5 w-4.5">
          <path d="M16.5 4.5 19.5 7.5 8 19H5v-3L16.5 4.5Z" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      <button onClick={onRemove} aria-label="Löschen" className="flex-shrink-0 text-ink-400 dark:text-night-faint">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4.5 w-4.5">
          <path d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2m-9 0 1 13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1l1-13" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </li>
  );
}
